import { useState } from "react";
import { PlayCircle } from "lucide-react";

/**
 * YouTube facade — hides YouTube's title/channel overlay by showing our own
 * poster + play button. Loads the iframe only after the user clicks play,
 * which also improves initial page-load performance.
 *
 * @param {string} id       YouTube video ID (e.g. "AyjC3_iyaK8")
 * @param {string} title    Accessible title for the iframe (not shown visually)
 * @param {string} [poster] Optional poster URL; defaults to YouTube maxres thumbnail
 * @param {boolean} [short] If true, uses the `hqdefault.jpg` (better for Shorts / vertical)
 */
export function YouTubeEmbed({ id, title, poster, short = false, className = "" }) {
  const [playing, setPlaying] = useState(false);
  const posterSrc =
    poster ||
    (short
      ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
      : `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`);

  return (
    <div className={`relative w-full h-full bg-black overflow-hidden ${className}`} data-testid={`yt-embed-${id}`}>
      {playing ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
          title={title}
          loading="lazy"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="w-full h-full block"
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play video: ${title}`}
          data-testid={`yt-play-${id}`}
          className="group absolute inset-0 w-full h-full flex items-center justify-center cursor-pointer"
          style={{ background: "linear-gradient(135deg,#0B1B30 0%,#1B4A7A 70%,#2E6BB0 100%)" }}
        >
          <img
            src={posterSrc}
            alt=""
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            onLoad={(e) => {
              // YouTube returns a 120x90 grey placeholder when a thumbnail doesn't exist.
              // Fall back to hqdefault; if that is also missing, hide the img and show the branded gradient.
              if (e.currentTarget.naturalWidth <= 120) {
                if (!e.currentTarget.dataset.fallback) {
                  e.currentTarget.dataset.fallback = "1";
                  e.currentTarget.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
                } else {
                  e.currentTarget.style.display = "none";
                }
              }
            }}
            onError={(e) => {
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = "1";
                e.currentTarget.src = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;
              } else {
                e.currentTarget.style.display = "none";
              }
            }}
          />
          <div className="absolute inset-0 bg-black/25 group-hover:bg-black/10 transition-colors" />
          <div className="relative flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-accent/95 shadow-2xl transition-transform duration-300 group-hover:scale-110">
            <PlayCircle className="w-14 h-14 md:w-16 md:h-16 text-white" strokeWidth={1.2} />
          </div>
        </button>
      )}
    </div>
  );
}
